import {
  Box,
  Button,
  Grid,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BentoBrand } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import BrandCard from "features/Influencer/Discover/Results/BrandCard";
import { BrandCardSource } from "features/Influencer/Discover/Results/schema";
import { trackEvent } from "utils/tracking";

import styles from "../styles";

interface SelectBrandFormProps {
  handleBack: () => void;
  handleNext: () => void;
  handleSkip?: () => void;
  onboardingForm: Map;
  updateSelectedBrand: (newBrand: BentoBrand | undefined) => void;
  recommendedBrands: BentoBrand[];
  refreshBrands: (restart: boolean) => void;
  loading: boolean;
}

const SelectBrandForm = ({
  handleBack,
  handleNext,
  handleSkip,
  updateSelectedBrand,
  refreshBrands,
  recommendedBrands,
  loading,
}: SelectBrandFormProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { currentOrg } = useContext(OrganizationUserContext);
  const { fetchOutreachTemplates } = useContext(OutreachTemplatesContext);
  const { setEmailBrandDialogOpen } = useContext(OnboardingDialogsContext);
  const { handleOpenQuickSendIndividual } = useContext(QuickSendDrawerContext);

  const selectBrand = async (bentoBrand: BentoBrand) => {
    updateSelectedBrand(bentoBrand);
    setEmailBrandDialogOpen(false);
    navigate(`/${routes.brands}`);
    handleOpenQuickSendIndividual(bentoBrand?.id, "Home", undefined, true);
  };

  useEffect(() => {
    refreshBrands(true);
    fetchOutreachTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const handleRefreshBrands = () => {
    trackEvent("Onboarding Form - Refresh Brands Button Clicked");
    refreshBrands(false);
  };

  return (
    <>
      <Typography
        variant={isMobileScreen ? "h4" : "h3"}
        sx={{ mb: { xs: 2, sm: 3 } }}
      >
        <Box
          component="i"
          color="secondary.dark"
          sx={{ pr: 2 }}
          className="fa-solid fa-paper-plane"
        />
        Let's send your first email!
      </Typography>

      <Typography gutterBottom sx={{ fontSize: { xs: 14, sm: 16 } }}>
        Select a brand to email
      </Typography>

      {loading && (
        <Grid container sx={styles.fieldContainer} spacing={1}>
          {Array.from({ length: 6 }, (x, i) => (
            <Grid item key={i} xs={12} md={6}>
              <Skeleton variant="rounded" sx={styles.loadingSkeleton} />
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && (
        <Grid
          container
          sx={styles.fieldContainer}
          alignItems="stretch"
          spacing={1}
        >
          {recommendedBrands.map((bentoBrand) => (
            <Grid item xs={12} md={6} key={bentoBrand.id}>
              <BrandCard
                brand={bentoBrand}
                source={BrandCardSource.ONBOARDING_BRAND}
                actions={
                  <Button
                    onClick={() => {
                      selectBrand(bentoBrand);
                    }}
                    variant="contained"
                    size="small"
                    disableElevation
                    sx={styles.quickSendButton}
                  >
                    Select
                  </Button>
                }
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Grid container justifyContent="flex-end" sx={styles.fieldContainer}>
        <Button color="inherit" onClick={handleRefreshBrands}>
          More
          <Box
            component="i"
            className="fa-solid fa-arrow-right"
            sx={{ ml: 1 }}
          />
        </Button>
      </Grid>

      <Grid item xs="auto" container columnGap={1}>
        <Grid item xs></Grid>
        {handleSkip && (
          <Grid item xs="auto">
            <Button color="secondary" onClick={handleSkip} disableElevation>
              Skip
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default SelectBrandForm;
